import React, { useState, useEffect } from "react";
import {
  Box,
  LinearProgress,
  LinearProgressProps,
  Typography,
} from "@mui/material";

interface PreloaderProps {
  messages?: Array<{
    text: string;
    duration: number;
  }>;
  renderProgressBar?: () => React.ReactNode;
}

const defaultMessages = [
  { text: "Fetching available seats…", duration: 2000 },
  { text: "Huddle up— loading your options…", duration: 2000 },
  { text: "Almost there— gathering seat availability…", duration: 2000 },
];

function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number }
) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontSize: "1rem" }}
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export function AnimatedTextPreloader({
  messages = defaultMessages,
  renderProgressBar,
}: PreloaderProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalProgress, setTotalProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let startTime: number;

    const calculateTotalDuration = () => {
      return messages.reduce((total, msg) => total + msg.duration, 0);
    };

    const startProgress = () => {
      startTime = Date.now();
      const totalDuration = calculateTotalDuration();

      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = now - startTime;
        const newProgress = Math.min((elapsed / totalDuration) * 100, 100);
        setTotalProgress(newProgress);

        // Calculate which message should be shown based on elapsed time
        let accumulatedTime = 0;
        let newIndex = 0;

        for (let i = 0; i < messages.length; i++) {
          accumulatedTime += messages[i].duration;
          if (elapsed < accumulatedTime) {
            newIndex = i;
            break;
          }
        }

        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
        }

        if (elapsed >= totalDuration) {
          clearInterval(interval);
          setIsComplete(true);
        }
      }, 10);
    };

    startProgress();

    return () => {
      clearInterval(interval);
    };
  }, [messages]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {renderProgressBar ? (
        renderProgressBar()
      ) : (
        <LinearProgressWithLabel
          variant="determinate"
          value={totalProgress}
          sx={{
            height: 16,
            borderRadius: 4,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
            "& .MuiLinearProgress-bar": {
              borderRadius: 4,
              transition: "transform 0.1s linear",
            },
          }}
        />
      )}

      <Box
        sx={{
          position: "relative",
          height: "40px",
          mt: 2,
        }}
      >
        <Typography
          variant="body1"
          align="center"
          fontSize={"1.1rem"}
          fontWeight={600}
        >
          {isComplete
            ? messages[messages.length - 1].text
            : messages[currentIndex].text}
        </Typography>
      </Box>
    </Box>
  );
}
